import {FC, PropsWithChildren, useCallback, useState, useEffect} from "react";

import {Button} from "../ui/Button";
import {Separator} from "../ui/separator";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {Input} from "@/components/ui/input";
import {cn} from "@/lib/utils";

export type Variable = {
  name: string;
  value?: string;
};

export type CustomActionModalProps = PropsWithChildren<{
  isOpen: boolean;
  variables: Variable[];
  onClose: () => void;
  onInitialize: (values: Record<string, string>) => void;
}>;

const CustomActionModal: FC<CustomActionModalProps> = ({
  children,
  isOpen,
  variables,
  onClose,
  onInitialize,
}) => {
  const [inputs, setInputs] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      const initialInputs = variables.reduce(
        (acc, v) => {
          acc[v.name] = v.value || "";
          return acc;
        },
        {} as Record<string, string>,
      );
      setInputs(initialInputs);
    }
  }, [isOpen, variables]);

  const handleInputChange = (name: string, value: string) => {
    setInputs(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose],
  );

  const isInitializeDisabled = Object.values(inputs).some(v => v.trim() === "");

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {children && (
        <DialogTrigger asChild onClick={e => e.stopPropagation()}>
          {children}
        </DialogTrigger>
      )}

      <DialogContent
        withoutClose
        className={cn("w-[648px] flex flex-col p-0", "rounded-xl border")}
        onClick={e => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle>
            Fill the test values to initialize the custom action
          </DialogTitle>
        </DialogHeader>

        <div className="p-5 flex flex-col gap-y-5">
          <div className="flex gap-5 p-2">
            <div className="flex-1 text-sm font-medium text-primary">
              Variable Name
            </div>
            <div className="flex-1 text-sm font-medium text-primary">
              Test Value
            </div>
          </div>
          <Separator />
          <div className="flex flex-col gap-y-1">
            {variables.map(variable => (
              <div key={variable.name} className="flex items-center gap-5 p-2">
                <div className="flex-1 text-sm font-medium">
                  {variable.name}
                </div>
                <div className="flex-1">
                  <Input
                    type="text"
                    value={inputs[variable.name] || variable.value || ""}
                    placeholder="Type here..."
                    onChange={e =>
                      handleInputChange(variable.name, e.target.value)
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <DialogClose asChild>
            <Button
              disabled={isInitializeDisabled}
              onClick={() => onInitialize(inputs)}
            >
              Initialize action
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomActionModal;
